import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import styles from "../../assets/styles/Aboutschool/AboutSchool.module.css";
import {
  Award,
  BookOpen,
  GraduationCap,
  Globe,
  Heart,
  Lightbulb,
  MapPin,
  Mountain,
  School,
  Shield,
  Sparkles,
  Star,
  Target,
  Users,
  Zap,
} from "lucide-react";

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/our-school`;

const EMPTY_SECTION = {
  title: "",
  image: "",
  imageAlt: "",
  contentHtml: "",
};

const DEFAULT_NAV = [
  { id: "about", label: "About School", iconKey: "School" },
  { id: "vision", label: "Vision & Mission", iconKey: "Target" },
  { id: "teaching", label: "Teaching", iconKey: "BookOpen" },
  { id: "values", label: "Values", iconKey: "Star" },
  { id: "edge", label: "Our Edge", iconKey: "Zap" },
];

const normalizeSchool = (school) => ({
  id: school?.id || "",
  name: school?.name || "",
  iconKey: school?.iconKey || "School",
  sections: {
    about: { ...EMPTY_SECTION, ...(school?.sections?.about || {}) },
    vision: { ...EMPTY_SECTION, ...(school?.sections?.vision || {}) },
    teaching: { ...EMPTY_SECTION, ...(school?.sections?.teaching || {}) },
    values: { ...EMPTY_SECTION, ...(school?.sections?.values || {}) },
    edge: { ...EMPTY_SECTION, ...(school?.sections?.edge || {}) },
  },
});

const normalizeData = (data) => ({
  hero: {
    title: data?.hero?.title || "About Our Schools",
    subtitle:
      data?.hero?.subtitle ||
      "Yoga Alliance Certified Teacher Training Schools in Rishikesh, India",
    omText: data?.hero?.omText || "ॐ",
  },
  sectionNav:
    data?.sectionNav?.length > 0
      ? data.sectionNav
      : DEFAULT_NAV,
  schools: Array.isArray(data?.schools) ? data.schools.map(normalizeSchool) : [],
});

const resolveAssetUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/uploads/")) return `${ENV_API_BASE_URL}${value}`;
  if (value.startsWith("uploads/")) return `${ENV_API_BASE_URL}/${value}`;
  return value;
};

const iconMap = {
  School,
  Heart,
  Mountain,
  Sparkles,
  Target,
  BookOpen,
  Star,
  Zap,
  Users,
  Award,
  Globe,
  MapPin,
  Shield,
  Lightbulb,
};

const renderIcon = (iconKey, size = 20, className) => {
  const IconComponent = iconMap[iconKey] || School;
  return <IconComponent size={size} className={className} />;
};

const AboutSchool = () => {
  const [pageData, setPageData] = useState(() => normalizeData(null));
  const [activeTab, setActiveTab] = useState("");
  const [activeSection, setActiveSection] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await axios.get(API);
        const latest = Array.isArray(res.data?.data)
          ? res.data.data[0]
          : Array.isArray(res.data)
          ? res.data[0]
          : res.data?.data || res.data || null;

        const normalized = normalizeData(latest);
        setPageData(normalized);
      } catch (fetchError) {
        console.error("Failed to load Our School page:", fetchError);
        setError("Failed to load school content.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const schools = pageData.schools;
  const sections = pageData.sectionNav;

  useEffect(() => {
    if (!schools.length) {
      setActiveTab("");
      return;
    }

    const hasActiveSchool = schools.some((school) => school.id === activeTab);
    if (!hasActiveSchool) {
      setActiveTab(schools[0].id || "");
    }
  }, [activeTab, schools]);

  useEffect(() => {
    if (!sections.length) {
      setActiveSection("");
      return;
    }

    const hasActiveSection = sections.some((section) => section.id === activeSection);
    if (!hasActiveSection) {
      setActiveSection(sections[0].id || "");
    }
  }, [activeSection, sections]);

  const activeSchool = useMemo(
    () => schools.find((school) => school.id === activeTab) || schools[0] || null,
    [activeTab, schools]
  );

  const activeSectionData = activeSchool?.sections?.[activeSection] || EMPTY_SECTION;
  const activeSectionMeta =
    sections.find((section) => section.id === activeSection) || sections[0] || null;

  return (
    <div className={styles.container}>
      <div className={styles.hero}>
        <div className={styles.heroContent}>
          <GraduationCap className={styles.heroIcon} size={80} />
          <h1 className={styles.heroTitle}>{pageData.hero.title}</h1>
          <p className={styles.heroSubtitle}>{pageData.hero.subtitle}</p>
          <div className={styles.heroOm}>{pageData.hero.omText}</div>
        </div>
      </div>

      {schools.length > 0 && (
        <div className={styles.tabSection}>
          <div className={styles.tabContainer}>
            {schools.map((school) => (
              <button
                key={school.id || school.name}
                className={`${styles.tab} ${
                  activeTab === school.id ? styles.tabActive : ""
                }`}
                onClick={() => {
                  setActiveTab(school.id);
                  setActiveSection(sections[0]?.id || "");
                }}
              >
                {renderIcon(school.iconKey)}
                <span>{school.name || "Untitled School"}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {schools.length > 0 && sections.length > 0 && (
        <div className={styles.sectionNav}>
          {sections.map((section) => (
            <button
              key={section.id}
              className={`${styles.sectionBtn} ${
                activeSection === section.id ? styles.sectionBtnActive : ""
              }`}
              onClick={() => setActiveSection(section.id)}
            >
              {renderIcon(section.iconKey, 18)}
              <span>{section.label}</span>
            </button>
          ))}
        </div>
      )}

      <div className={styles.content}>
        {loading && (
          <div className={styles.schoolCard}>
            <h2 className={styles.cardTitle}>Loading school content...</h2>
          </div>
        )}

        {!loading && error && (
          <div className={styles.schoolCard}>
            <h2 className={styles.cardTitle}>Unable to load page</h2>
            <div className={styles.contentText}>
              <p>{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && !activeSchool && (
          <div className={styles.schoolCard}>
            <h2 className={styles.cardTitle}>No school content available</h2>
            <div className={styles.contentText}>
              <p>
                This page is ready, but no school tabs have been saved from the
                admin panel yet.
              </p>
            </div>
          </div>
        )}

        {!loading && !error && activeSchool && activeSectionMeta && (
          <div className={styles.schoolCard}>
            <div className={styles.cardHeader}>
              {renderIcon(activeSectionMeta.iconKey, 40, styles.headerIcon)}
              <h2 className={styles.cardTitle}>
                {activeSectionData.title ||
                  `${activeSectionMeta.label} - ${activeSchool.name}`}
              </h2>
            </div>

            {activeSectionData.image && (
              <div className={styles.imageSection}>
                <img
                  src={resolveAssetUrl(activeSectionData.image)}
                  alt={
                    activeSectionData.imageAlt ||
                    activeSectionData.title ||
                    activeSchool.name
                  }
                  className={styles.heroImage}
                />
              </div>
            )}

            <div className={styles.contentText}>
              {activeSectionData.contentHtml ? (
                <div
                  className={styles.htmlContent}
                  dangerouslySetInnerHTML={{
                    __html: activeSectionData.contentHtml,
                  }}
                />
              ) : (
                <p>
                  Content for this section has not been added yet. Update it
                  from the admin panel to make this section live.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <div className={styles.footerContent}>
          <GraduationCap size={40} />
          <p className={styles.footerText}>
            {pageData.hero.omText} Discover our schools through dynamic content
            managed from the admin panel.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AboutSchool;
